import { getClientLocale, withLocale } from "./client";

export const REPORT_DIMENSIONS = [
  "genus",
  "species",
  "isolation_country",
  "isolation_region",
  "isolation_location",
  "isolation_facility",
  "host",
  "clade",
  "growth_medium",
  "cryobox",
  "env_clinical",
  "migration_status",
] as const;

export type ReportDimension = (typeof REPORT_DIMENSIONS)[number];

export const GEO_REPORT_LEVELS = [
  "isolation_country",
  "isolation_region",
  "isolation_location",
  "isolation_facility",
] as const;

export type GeoReportLevel = (typeof GEO_REPORT_LEVELS)[number];

export type StrainReportSlice = {
  label: string;
  count: number;
};

export type StrainReportPivotRow = {
  label_a: string;
  counts: Record<string, number>;
  total: number;
};

export type StrainReport = {
  total: number;
  dimension_a: ReportDimension;
  dimension_b: ReportDimension | null;
  slices: StrainReportSlice[] | null;
  pivot: {
    column_labels: string[];
    rows: StrainReportPivotRow[];
  } | null;
};

export type StrainGeoReportPoint = {
  label: string;
  count: number;
  latitude: number;
  longitude: number;
};

export type StrainGeoReport = {
  level: GeoReportLevel;
  total: number;
  mapped: number;
  unmapped: number;
  empty: number;
  points: StrainGeoReportPoint[];
};

export async function fetchStrainReport(params: {
  dimension_a: ReportDimension;
  dimension_b?: ReportDimension;
  limit?: number;
}): Promise<StrainReport> {
  const query = new URLSearchParams({ dimension_a: params.dimension_a });
  if (params.dimension_b) {
    query.set("dimension_b", params.dimension_b);
  }
  if (params.limit) {
    query.set("limit", String(params.limit));
  }
  const locale = getClientLocale();
  const response = await fetch(withLocale(`/api/strains/report?${query.toString()}`), {
    credentials: "include",
    headers: { "Accept-Language": locale ?? "en" },
  });
  if (!response.ok) {
    throw new Error(`Report failed (${response.status})`);
  }
  return response.json() as Promise<StrainReport>;
}

export async function fetchStrainGeoReport(level: GeoReportLevel): Promise<StrainGeoReport> {
  const query = new URLSearchParams({ level });
  const locale = getClientLocale();
  const response = await fetch(withLocale(`/api/strains/report/geo?${query.toString()}`), {
    credentials: "include",
    headers: { "Accept-Language": locale ?? "en" },
  });
  if (!response.ok) {
    throw new Error(`Geo report failed (${response.status})`);
  }
  return response.json() as Promise<StrainGeoReport>;
}

export type StrainCoverageItem = {
  key: string;
  group: string;
  count: number;
  total: number;
  percent: number;
  href: string;
};

export type StrainCoverageGroup = {
  key: string;
  items: StrainCoverageItem[];
};

export type StrainCoverageReport = {
  total: number;
  groups: StrainCoverageGroup[];
};

export type StrainWorkRow = {
  key: string;
  count: number;
  href: string;
};

export type StrainWorkReport = {
  total: number;
  not_ready: number;
  by_status: StrainWorkRow[];
  queues: StrainWorkRow[];
};

export async function fetchStrainCoverageReport(): Promise<StrainCoverageReport> {
  const locale = getClientLocale();
  const response = await fetch(withLocale("/api/strains/report/coverage"), {
    credentials: "include",
    headers: { "Accept-Language": locale ?? "en" },
  });
  if (!response.ok) {
    throw new Error(`Coverage report failed (${response.status})`);
  }
  return response.json() as Promise<StrainCoverageReport>;
}

export async function fetchStrainWorkReport(): Promise<StrainWorkReport> {
  const locale = getClientLocale();
  const response = await fetch(withLocale("/api/strains/report/work"), {
    credentials: "include",
    headers: { "Accept-Language": locale ?? "en" },
  });
  if (!response.ok) {
    throw new Error(`Work report failed (${response.status})`);
  }
  return response.json() as Promise<StrainWorkReport>;
}
